import * as React from 'react';
import { Slot as SlotPrimitive } from 'radix-ui';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
	// Custom: we change transition-all for transition-colors, so we don't transition width for when we show loading icon
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
	{
		variants: {
			variant: {
				default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
				destructive:
					'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
				outline:
					'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
				outlinePrimary:
					'border border-primary text-primary bg-background shadow-xs hover:bg-accent dark:bg-input/30 dark:hover:bg-input/50',
				outlineAlt:
					'border border-foreground bg-transparent! shadow-xs hover:bg-accent dark:bg-input/30 dark:hover:bg-input/50',
				secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
				ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
				link: 'text-primary underline-offset-4 hover:underline',
				dark: 'bg-dark text-dark-foreground ring-2 ring-accent/50',
				darkAlt: 'bg-dark-alt text-dark-alt-foreground ring-2 ring-accent/50',
			},
			size: {
				'default': 'h-9 px-4 py-2 has-[>svg]:px-3',
				'sm': 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
				'lg': 'h-10 rounded-[12px] px-6 has-[>svg]:px-4',
				'xl': "h-12 rounded-[12px] text-base px-6 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-6",
				'2xl': "h-14 rounded-[12px] text-base px-6 has-[>svg]:px-4 [&_svg:not([class*='size-'])]:size-7",
				'icon-sm': 'size-7 rounded-full!',
				'icon': 'size-9',
			},
		},
		defaultVariants: {
			variant: 'default',
			size: 'default',
		},
	},
);

export type ButtonProps = React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
	};

function Button({
	className,
	variant,
	size,
	asChild = false,
	children,
	...props
}: React.ComponentProps<'button'> &
	VariantProps<typeof buttonVariants> & {
		asChild?: boolean;
		loading?: boolean;
	}) {
	const Comp = asChild ? SlotPrimitive.Slot : 'button';

	return (
		<Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }))} {...props}>
			{children}
		</Comp>
	);
}

export { Button, buttonVariants };
